import React, { useMemo, useEffect, useState } from "react";
import { Text, Checkbox, Heading, DatePicker } from "@telia/teddy";
import cn from "classnames";
import addDays from "date-fns/addDays";
import addHours from "date-fns/addHours";
import differenceInDays from "date-fns/differenceInDays";
import format from "date-fns/format";
import getYear from "date-fns/getYear";
import isWeekend from "date-fns/isWeekend";
import nb from "date-fns/locale/nb";
import max from "date-fns/max";
import _get from "lodash/get";
import _isEmpty from "lodash/isEmpty";
import { getHoursOpen, getHolidaysForGivenMonth } from "./utils";
import { HolidayData } from "../../../../../../models";
import { UpdatePortingDatePortingInfo } from "../../../../../../models/state";
import { getDefaultPortinDate } from "../../../../../../shared/util";
import { PhoneNumberFormatters } from "../../../../app/utils";
import { MAX_PORTING_DATE_DAYS } from "../../../../../../constants/porting";
import "./PortingCard.less";
import isSameDay from "date-fns/isSameDay";
import parseISO from "date-fns/parseISO";
import TimePicker from "../../../../components/components/molecules/TimePicker";

const formatterPhone = PhoneNumberFormatters[0].displayFn;

interface PortingCardProps {
    creationDate?: string;
    holidayData: HolidayData;
    isAllowedToUpdate?: boolean;
    isEsim?: boolean;
    numberOfDays?: number;
    phoneNumber?: string;
    portingInfo?: UpdatePortingDatePortingInfo;
    selectedDate: Date;
    setSelectedDate: (date: Date) => void;
    setSelectedDateForAllSubscriptions?: (date: Date) => void;
    shouldShowSameCheckBoxOnChanged?: boolean;
    shouldShowSameCheckBoxValue?: boolean;
}

const PortingCard: React.FC<PortingCardProps> = ({
    phoneNumber,
    portingInfo,
    isAllowedToUpdate,
    holidayData,
    numberOfDays = MAX_PORTING_DATE_DAYS,
    shouldShowSameCheckBoxOnChanged = false,
    shouldShowSameCheckBoxValue = false,
    selectedDate,
    creationDate,
    setSelectedDate,
    setSelectedDateForAllSubscriptions,
    isEsim,
}) => {
    const originalDate = new Date(creationDate || 0);
    const actualDateString = portingInfo?.actualDate;
    const actualDate = new Date(actualDateString || 0);
    const newDateString = portingInfo?.newDate;
    const newDate = new Date(_get(portingInfo, "newDate", 0));
    const isChanged = newDateString && actualDate.getTime() !== newDate.getTime();
    const [now, setNow] = useState(() => new Date());
    const endDate = addDays(originalDate, numberOfDays);
    const earliestPossibleDate = isEsim
        ? new Date(getDefaultPortinDate(isEsim))
        : addHours(max([originalDate, now]), 26);
    const startDate = earliestPossibleDate;
    // Let user know about SIM-card needed if they try and change to close to original date of order
    const showSimCardMessage = differenceInDays(selectedDate, originalDate) < 5;
    const hoursOpen = getHoursOpen(selectedDate, isEsim);

    const selectedYearMonth = format(selectedDate, "yyyy-MM");
    const holidaysForCurrentMonth = useMemo(() => {
        return getHolidaysForGivenMonth(selectedDate, holidayData);
    }, [selectedYearMonth, holidayData]);

    // Keep the earliest selectable date/time fresh in case the user stays on this page across a minute boundary
    useEffect(() => {
        const interval = setInterval(() => setNow(new Date()), 10_000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        if (_isEmpty(hoursOpen)) return;

        const currentValue = format(selectedDate, "HH:mm");
        const optionValues = hoursOpen.map((d) => format(d, "HH:mm"));

        if (optionValues.includes(currentValue)) return;

        const nearest = hoursOpen.reduce((prev, curr) =>
            Math.abs(curr.getTime() - selectedDate.getTime()) < Math.abs(prev.getTime() - selectedDate.getTime())
                ? curr
                : prev,
        );

        const updated = new Date(selectedDate);
        updated.setHours(nearest.getHours(), nearest.getMinutes(), 0, 0);
        setSelectedDate(updated);
    }, [format(selectedDate, "yyyy-MM-dd"), startDate.getTime()]);

    return (
        <div className={cn("porting-card")}>
            {phoneNumber && (
                <Heading mb="100" className="porting-card__heading" as="h2" variant="title-100" data-di-mask>
                    {formatterPhone(phoneNumber)}
                </Heading>
            )}
            {isAllowedToUpdate && (
                <div className="porting-card__content">
                    <div className="porting-card__input-fields">
                        <div data-di-unmask-field className="porting-card__date-picker">
                            <DatePicker
                                value={format(selectedDate, "yyyy-MM-dd")}
                                min={format(startDate, "yyyy-MM-dd")}
                                max={format(endDate, "yyyy-MM-dd")}
                                isDateDisabled={(date) => {
                                    if (!date) return false;
                                    const yearIndex = getYear(date);
                                    const isHoliday = (holidayData[yearIndex] || []).some((holiday) =>
                                        isSameDay(parseISO(holiday.date), date),
                                    );
                                    return isWeekend(date) || isHoliday;
                                }}
                                onSelectedDate={(date) => {
                                    const updated = new Date(date);
                                    updated.setHours(selectedDate.getHours(), selectedDate.getMinutes(), 0, 0);
                                    setSelectedDate(updated);
                                }}
                                label="Dato"
                            />
                        </div>
                        {/* Hour/minute dropdown */}
                        <div className="porting-card__time-picker">
                            <TimePicker
                                listOfHours={hoursOpen}
                                selectedDate={selectedDate}
                                setSelectedDate={setSelectedDate}
                            />
                        </div>
                    </div>
                    {isChanged && shouldShowSameCheckBoxOnChanged && setSelectedDateForAllSubscriptions && (
                        <Checkbox className="porting-card__checkbox">
                            <Checkbox.Trigger
                                checked={shouldShowSameCheckBoxValue}
                                onCheckedChange={() => setSelectedDateForAllSubscriptions(selectedDate)}
                            />
                            Jeg ønsker samme overføringstidspunkt for alle numrene
                        </Checkbox>
                    )}
                </div>
            )}
            {holidaysForCurrentMonth?.length > 0 && (
                <div className="porting-card__date-picker-holidays">
                    {holidaysForCurrentMonth.map((holiday) => (
                        <span key={holiday.date.format("YYYY/MM/DD")}>
                            <span className="telia-sans-medium">{holiday.date.format("DD/MM")}</span> {holiday.name}
                        </span>
                    ))}
                </div>
            )}
            {showSimCardMessage && !isEsim && (
                <Text className="porting-card__sim-card-message">
                    Husk at du må ha fått SIM-kort før du kan gjennomføre flyttingen.
                </Text>
            )}
            {!isAllowedToUpdate && (
                <Text>
                    Ikke mulig å endre overføringsdato for dette telefonnummeret. <br />
                    {actualDateString && (
                        <>
                            Overføring:{" "}
                            <span className="telia-sans-medium">
                                {format(actualDate, "dd.MM.yyyy kk:mm", { locale: nb })}
                            </span>
                        </>
                    )}
                    {!actualDateString && (
                        <>
                            Status: <span className="telia-sans-medium">Under behandling</span>
                        </>
                    )}
                </Text>
            )}
        </div>
    );
};

export default PortingCard;
